import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAircraft, getAllAircraft } from '../services/api';
import topLogo from '../assets/toplogo.svg';
import syncOnIcon from '../assets/icons/syncon.svg';
import menuIcon from '../assets/icons/menu.svg';
import './TopBar.css';

const TopBar = ({ menuOpen = false, onMenuToggle, aircraftLocked = false, refreshSignal = 0 }) => {
  const { user, aircraft, switchAircraft } = useAuth();
  const [allAircraft, setAllAircraft] = useState([]);
  const [acDropOpen, setAcDropOpen] = useState(false);
  const [acSearch, setAcSearch] = useState('');
  const [syncStatus] = useState('OK');
  const dropRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    getAllAircraft().then(r => setAllAircraft(r.data.aircraft || [])).catch(() => {});
  }, [refreshSignal]);

  useEffect(() => {
    if (!aircraft?.id) return undefined;

    let cancelled = false;
    getAircraft(aircraft.id)
      .then(res => {
        if (cancelled || !res.data.aircraft) return;
        const refreshedAircraft = res.data.aircraft;
        switchAircraft(refreshedAircraft);
        setAllAircraft(prev => prev.map(ac => (
          String(ac.id) === String(refreshedAircraft.id) ? { ...ac, ...refreshedAircraft } : ac
        )));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [aircraft?.id, refreshSignal, switchAircraft]);

  useEffect(() => {
    const handler = e => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setAcDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const onKeyDown = e => {
      if (e.key === 'Escape') {
        setAcDropOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (!acDropOpen) return;
    searchInputRef.current?.focus();
  }, [acDropOpen]);

  useEffect(() => {
    if (menuOpen) setAcDropOpen(false);
  }, [menuOpen]);

  const handleSelectAircraft = ac => {
    if (aircraftLocked) return;
    switchAircraft(ac);
    setAcDropOpen(false);
    setAcSearch('');
  };

  const filteredAircraft = allAircraft.filter(ac => {
    const term = acSearch.trim().toLowerCase();
    if (!term) return true;
    return (
      String(ac.registration || '').toLowerCase().includes(term) ||
      String(ac.ac_type || '').toLowerCase().includes(term)
    );
  });
  const selectedFlightHours = aircraft?.flight_hours ?? 0;
  const selectedFlightCycles = aircraft?.flight_cycles ?? 0;

  return (
    <header className="topbar">
      <div className="topbar-left">
        <div className="topbar-menu-wrap">
          <button
            type="button"
            className="topbar-menu-box topbar-menu-trigger"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            onClick={() => {
              setAcDropOpen(false);
              onMenuToggle?.();
            }}
          >
            <img className="topbar-menu-icon app-icon app-icon-menu" src={menuIcon} alt="" />
          </button>
        </div>
          <img className="topbar-logo" src={topLogo} alt="Top logo" />
      </div>

      <div className="topbar-center">
        <span className="topbar-label">Cockpit Mode</span>
        <div className="topbar-sep" />

        <div className="topbar-ac-wrap" ref={dropRef}>
          <button
            className="topbar-ac-btn"
            disabled={aircraftLocked}
            title={aircraftLocked ? 'Aircraft cannot be changed while a flight is active' : undefined}
            onClick={() => {
              if (aircraftLocked) return;
              setAcDropOpen(v => !v);
              if (acDropOpen) setAcSearch('');
            }}
          >
            <span className="topbar-ac-reg">
              {aircraft ? `${aircraft.registration} • ${selectedFlightHours} FH / ${selectedFlightCycles} FC` : '— Select —'}
            </span>
            <span className={`topbar-chevron ${acDropOpen ? 'open' : ''}`} aria-hidden="true">▾</span>
          </button>

          <div className={`topbar-ac-dropdown ${!aircraftLocked && acDropOpen ? 'open' : 'closed'}`}>
            <div className="topbar-ac-search-wrap">
              <input
                ref={searchInputRef}
                type="text"
                className="topbar-ac-search"
                value={acSearch}
                onChange={e => setAcSearch(e.target.value)}
                placeholder="Search registration or type"
              />
            </div>

            <div className="topbar-ac-list">
              {filteredAircraft.map(ac => (
                <button
                  key={ac.id}
                  className={`topbar-ac-option ${aircraft?.id === ac.id ? 'active' : ''}`}
                  onClick={() => handleSelectAircraft(ac)}
                >
                  <span className="option-reg">{ac.registration}</span>
                  <span className="option-type">{ac.ac_type}</span>
                </button>
              ))}
              {filteredAircraft.length === 0 && (
                <div className="topbar-ac-empty">No matching aircraft</div>
              )}
            </div>
          </div>
        </div>

        <div className="topbar-sep" />
        <div className={`topbar-sync ${syncStatus === 'OK' ? 'ok' : 'err'}`}>
          <img src={syncOnIcon} alt="" className="topbar-sync-icon app-icon app-icon-syncon" />
          Sync {syncStatus}
        </div>
      </div>

      <div className="topbar-right">
        <span className="topbar-user">{user?.full_name || user?.email || 'Pilot'}</span>
      </div>
    </header>
  );
};

export default TopBar;


